/* eslint-disable @typescript-eslint/indent */
import { Button, FormControl, FormErrorMessage, FormLabel, Input, Popover, PopoverBody, PopoverContent, PopoverTrigger, Select, Stack, Text, Textarea, useToast, VStack } from "@chakra-ui/react"
import { format } from "date-fns"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { DateRange } from "react-date-range"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { GenerateUserDetailsReportMutationVariables, UserStatus, useGenerateUserDetailsReportMutation } from "../graphql"

type GenerateUserDetailsReportFormValues = GenerateUserDetailsReportMutationVariables["input"]

const userStatusOptions = Object.values(UserStatus)

const validationSchema = yup.object<Record<keyof GenerateUserDetailsReportFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	label: yup.object({
		name: yup.string().required().label("Name"),
		description: yup.string().nullable().label("Description"),
	}),
	sinceDate: yup.date().required().label("Since Date"),
	untilDate: yup.date().required().label("Until Date"),
	userStatus: yup
		.string()
		.oneOf([...userStatusOptions])
		.nullable()
		.optional(),
})

const initialValues: GenerateUserDetailsReportFormValues = {
	label: {
		name: "",
		description: "",
	},
	sinceDate: new Date(),
	untilDate: new Date(),
	userStatus: undefined,
}

export const GenerateUserDetailsReportForm: React.FC = () => {
	const [{ fetching }, generateUserDetailsReport] = useGenerateUserDetailsReportMutation()

	const toast = useToast()
	const navigate = useNavigate()

	const onSubmit = async (values: GenerateUserDetailsReportFormValues) => {
		const input: GenerateUserDetailsReportFormValues = {
			...values,
			userStatus: values.userStatus ? values.userStatus : undefined,
		}

		const { data, error } = await generateUserDetailsReport({ input })

		if (error) {
			return toast({
				description: error.message.replace("[GraphQL] ", ""),
				status: "error",
			})
		}

		if (data?.generateUserDetailsReport) {
			navigate(`/reports/past/${data.generateUserDetailsReport._id}`, { replace: true })
		}
	}

	const formik = useFormik<GenerateUserDetailsReportFormValues>({ initialValues, validationSchema, onSubmit })

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<Stack w="full" direction={{ base: "column", xl: "row" }}>
				<VStack w="full" align="stretch">
					<FormControl isInvalid={Boolean(formik.touched.label?.name && formik.errors.label?.name)} isRequired>
						<FormLabel fontWeight="bold">Name</FormLabel>

						<Input variant="filled" bgColor="grayscale.input-background" placeholder="Enter name" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("label.name")} />

						<FormErrorMessage>{formik.errors.label?.name}</FormErrorMessage>
					</FormControl>
					<FormControl isInvalid={Boolean(formik.touched.label?.description && formik.errors.label?.description)}>
						<FormLabel fontWeight="bold">Description</FormLabel>

						<Textarea variant="filled" bgColor="grayscale.input-background" placeholder="Enter description" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("label.description")} />

						<FormErrorMessage>{formik.errors.label?.description}</FormErrorMessage>
					</FormControl>
					<Popover>
						<PopoverTrigger>
							{formik.values.sinceDate && formik.values.untilDate && (
								<FormControl>
									<FormLabel fontWeight="bold">Date Range</FormLabel>

									<Input variant="filled" bgColor="grayscale.input-background" value={`${format(formik.values.sinceDate, "MMM dd, yyyy")} - ${format(formik.values.untilDate, "MMM dd, yyyy")}`} isReadOnly />
								</FormControl>
							)}
						</PopoverTrigger>
						<PopoverContent w="full" _focus={{ shadow: "none" }}>
							<PopoverBody w="full" p="0">
								<DateRange
									editableDateInputs={true}
									onChange={(item) => {
										if (item?.selection.startDate && item?.selection.endDate) {
											formik.setFieldValue("sinceDate", new Date(item.selection.startDate))
											formik.setFieldValue("untilDate", new Date(item.selection.endDate))
										}
									}}
									moveRangeOnFirstSelection={false}
									ranges={[{ startDate: formik.values.sinceDate, endDate: formik.values.untilDate, key: "selection" }]}
									maxDate={new Date()}
								/>
							</PopoverBody>
						</PopoverContent>
					</Popover>
				</VStack>
				<VStack w="full" align="stretch">
					<FormControl>
						<FormLabel fontWeight="bold">User Status</FormLabel>

						<Select
							variant="filled"
							bgColor="grayscale.input-background"
							value={formik.values.userStatus ?? ""}
							onChange={(event) => {
								const value = event.target.value as UserStatus | ""
								formik.setFieldValue("userStatus", value === "" ? undefined : value)
							}}
						>
							<option value="">All statuses</option>
							<option value={UserStatus.Active}>Active</option>
							<option value={UserStatus.Inactive}>Inactive</option>
						</Select>

						<FormErrorMessage>{formik.errors.userStatus}</FormErrorMessage>
					</FormControl>
					<Button type="submit" colorScheme="primary" isLoading={fetching}>
						Generate
					</Button>
					<Text fontSize="sm" color="grayscale.placeholer">
						The report includes every user in your site. Use the status filter above to limit the report to only active or inactive accounts.
					</Text>
				</VStack>
			</Stack>
		</VStack>
	)
}
